import dynamic from 'next/dynamic';
import type { Dispatch, FC, ReactNode, RefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Loader } from '@/components/Loader';
import { ShoppingBag } from '@/components/ShoppingBag';
import type { CheckoutFragment, MoneyFragment } from '@/graphql/saleor/api';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import {
  COUNTRYCODE_TO_SHOW_SHIPPING_AND_TAXNOTE_MESSAGE,
  GLOBAL_E_MARKET_AVAILABLE,
  ProductTypeSlug,
  VOUCHER_TYPE,
} from '@/lib/const';
import type { ExpressWalletStatus } from '@/lib/expressWalletReadiness';
import { type PreOrderData } from '@/lib/preOrder';
import { checkIfVatIncluded } from '@/lib/regions';
import type { ApiError, ApiErrorType, ApiErrorWithType } from '@/lib/types';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import type { OrderDataFragment } from './TransactionSummary.utils';
import {
  calculateSubtotal,
  formatCheckoutOrder,
  getCheckoutToPayLater,
  getDepositPrice,
  getOrderToPayLater,
  getReverseDiscount,
  isOrder,
} from './TransactionSummary.utils';

const EXPRESS_SECTION_PRELOAD_MARGIN = '100px';

type AtomicExpressPresentation = 'hidden' | 'loading' | 'ready';
type WalletStatuses = {
  applePay: ExpressWalletStatus;
  paypal: ExpressWalletStatus;
};

const getExpectedWalletStatuses = ({
  displayApplePay,
  displayPaypal,
  statuses,
}: {
  displayApplePay: boolean;
  displayPaypal: boolean;
  statuses: WalletStatuses;
}): ExpressWalletStatus[] => {
  const expectedStatuses: ExpressWalletStatus[] = [];
  if (displayPaypal) expectedStatuses.push(statuses.paypal);
  if (displayApplePay) expectedStatuses.push(statuses.applePay);
  return expectedStatuses;
};

const getAtomicExpressPresentation = ({
  expectedStatuses,
  mountExpressButtons,
  showLoadingPlaceholder,
}: {
  expectedStatuses: ExpressWalletStatus[];
  mountExpressButtons: boolean;
  showLoadingPlaceholder: boolean;
}): AtomicExpressPresentation => {
  if (!mountExpressButtons) {
    return showLoadingPlaceholder ? 'loading' : 'hidden';
  }
  if (expectedStatuses.includes('waiting')) return 'loading';
  return expectedStatuses.includes('ready') ? 'ready' : 'hidden';
};

const PaypalExpressButton = dynamic(
  () =>
    import('../PaypalExpressButton/PaypalExpressButton').then(
      (mod) => mod.PaypalExpressButton,
    ),
  { ssr: false, loading: () => <Loader height={48} size='sm' /> },
);

const ApplePayExpressButton = dynamic(
  () =>
    import('@/components/ApplePayExpressButton/ApplePayExpressButton').then(
      (mod) => mod.ApplePayExpressButton,
    ),
  { ssr: false, loading: () => <Loader height={48} size='sm' /> },
);

const ExpressWalletButtons = ({
  areBraceletSizeErrorsSet,
  displayApplePay,
  displayPaypal,
  errorHandler,
  isExpressCheckoutBlocked,
  onApplePayStatusChange,
  onPaypalStatusChange,
}: {
  areBraceletSizeErrorsSet: () => boolean;
  displayApplePay: boolean;
  displayPaypal: boolean;
  errorHandler: TransactionSummaryProps['errorHandler'];
  isExpressCheckoutBlocked: boolean;
  onApplePayStatusChange?: (status: ExpressWalletStatus) => void;
  onPaypalStatusChange?: (status: ExpressWalletStatus) => void;
}) => (
  <>
    {displayPaypal && (
      <PaypalExpressButton
        areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
        data-test-id='ShoppingBagDrawerPaypalExpressButton'
        errorHandler={errorHandler}
        isExpressCheckoutBlocked={isExpressCheckoutBlocked}
        onStatusChange={onPaypalStatusChange}
        zIndex={1}
      />
    )}
    {displayApplePay && (
      <ApplePayExpressButton
        areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
        data-test-id='ShoppingBagDrawerAppleExpressButton'
        errorHandler={errorHandler}
        isExpressCheckoutBlocked={isExpressCheckoutBlocked}
        onStatusChange={onApplePayStatusChange}
      />
    )}
  </>
);

const INITIAL_WALLET_STATUSES: WalletStatuses = {
  applePay: 'waiting',
  paypal: 'waiting',
};

type WalletStatusSnapshot = {
  key: string;
  statuses: WalletStatuses;
};

const getExpectedWalletKey = (
  displayPaypal: boolean,
  displayApplePay: boolean,
) => {
  const paypalKey = displayPaypal ? 'paypal' : '';
  const applePayKey = displayApplePay ? 'applePay' : '';
  return `${paypalKey}:${applePayKey}`;
};

const resetWalletSnapshot = (
  current: WalletStatusSnapshot,
  expectedWalletKey: string,
): WalletStatusSnapshot => {
  const isAlreadyReset =
    current.key === expectedWalletKey &&
    current.statuses.applePay === 'waiting' &&
    current.statuses.paypal === 'waiting';
  if (isAlreadyReset) return current;

  return { key: expectedWalletKey, statuses: INITIAL_WALLET_STATUSES };
};

const withWalletStatus = (
  current: WalletStatusSnapshot,
  liveKey: string,
  expectedWalletKey: string,
  wallet: keyof WalletStatuses,
  status: ExpressWalletStatus,
): WalletStatusSnapshot => {
  if (liveKey !== expectedWalletKey) return current;

  const statuses =
    current.key === expectedWalletKey
      ? current.statuses
      : INITIAL_WALLET_STATUSES;
  if (current.key === expectedWalletKey && statuses[wallet] === status) {
    return current;
  }

  return {
    key: expectedWalletKey,
    statuses: { ...statuses, [wallet]: status },
  };
};

const resolveVisibleWalletStatuses = (
  atomicExpressReveal: boolean,
  mountExpressButtons: boolean,
  statusSnapshot: WalletStatusSnapshot,
  expectedWalletKey: string,
) => {
  const canUseSnapshot =
    atomicExpressReveal &&
    mountExpressButtons &&
    statusSnapshot.key === expectedWalletKey;
  if (canUseSnapshot) return statusSnapshot.statuses;

  return INITIAL_WALLET_STATUSES;
};

const useExpressWalletStatuses = ({
  atomicExpressReveal,
  displayApplePay,
  displayPaypal,
  mountExpressButtons,
}: {
  atomicExpressReveal: boolean;
  displayApplePay: boolean;
  displayPaypal: boolean;
  mountExpressButtons: boolean;
}) => {
  const expectedWalletKey = getExpectedWalletKey(
    displayPaypal,
    displayApplePay,
  );
  const expectedWalletKeyRef = useRef(expectedWalletKey);
  expectedWalletKeyRef.current = expectedWalletKey;
  const [statusSnapshot, setStatusSnapshot] = useState<WalletStatusSnapshot>({
    key: '',
    statuses: INITIAL_WALLET_STATUSES,
  });

  useEffect(() => {
    if (atomicExpressReveal && mountExpressButtons) return;

    setStatusSnapshot((current) =>
      resetWalletSnapshot(current, expectedWalletKey),
    );
  }, [atomicExpressReveal, expectedWalletKey, mountExpressButtons]);

  const setWalletStatus = useCallback(
    (wallet: keyof WalletStatuses, status: ExpressWalletStatus) => {
      setStatusSnapshot((current) =>
        withWalletStatus(
          current,
          expectedWalletKeyRef.current,
          expectedWalletKey,
          wallet,
          status,
        ),
      );
    },
    [expectedWalletKey],
  );
  const setApplePayStatus = useCallback(
    (status: ExpressWalletStatus) => setWalletStatus('applePay', status),
    [setWalletStatus],
  );
  const setPaypalStatus = useCallback(
    (status: ExpressWalletStatus) => setWalletStatus('paypal', status),
    [setWalletStatus],
  );

  const statuses = resolveVisibleWalletStatuses(
    atomicExpressReveal,
    mountExpressButtons,
    statusSnapshot,
    expectedWalletKey,
  );

  return { setApplePayStatus, setPaypalStatus, statuses };
};

export interface TransactionSummaryProps {
  areBraceletSizeErrorsSet?: () => boolean;
  /** Reveal eligible express wallets together after provider UI is ready. */
  atomicExpressReveal?: boolean;
  channel?: string;
  coSalesTax?: MoneyFragment;
  code?: string;
  errorHandler?: (
    errors: ApiError[],
    opts?: { errorType?: ApiErrorType },
  ) => void;
  /** Skip intersection-based mounting when express checkout should initialize eagerly. */
  expressLazyMount?: boolean;
  fragment?: Partial<CheckoutFragment> | OrderDataFragment | null;
  isCheckoutSelectedAddress?: boolean;
  isColoradoBilling?: boolean;
  isDeposit?: boolean;
  /** Blocks express activation without unmounting PayPal / Apple Pay. */
  isExpressCheckoutBlocked?: boolean;
  isPricePending?: boolean;
  isProcessing?: boolean;
  mountExpressButtons?: boolean;
  preOrderData?: PreOrderData | null;
  salesTax?: MoneyFragment;
  setCheckoutValidationErrors?: Dispatch<SetStateAction<ApiErrorWithType[]>>;
  showButton?: boolean;
  showCompactSummary?: boolean;
  showExpressButton?: boolean;
  /** Preserves wallet layout without initializing wallet components. */
  showExpressLoadingPlaceholder?: boolean;
  showGiftBox?: boolean;
  showPadding?: boolean;
  showPaymentLogos?: boolean;
  showPaymentMethodsSection?: boolean;
  /** BEC-24915: the drawer renders this inside its scroll area instead. */
  showShippingAndTaxNote?: boolean;
  showSubtotalAndTaxes?: boolean;
  showTax: boolean;
  showTaxCalculationInfo?: boolean;
  voucherType?: string;
}

type ExpressCheckoutSectionProps = {
  areBraceletSizeErrorsSet: () => boolean;
  atomicExpressReveal: boolean;
  displayApplePayExpressButton: boolean;
  displayPaypalExpressButton: boolean;
  errorHandler: TransactionSummaryProps['errorHandler'];
  expressLazyMount: boolean;
  isExpressCheckoutBlocked: boolean;
  mountExpressButtons: boolean;
  showLoadingPlaceholder: boolean;
};

type ExpressCheckoutBranchProps = Omit<
  ExpressCheckoutSectionProps,
  'atomicExpressReveal' | 'expressLazyMount'
> & {
  expressRef: RefObject<HTMLDivElement>;
  isExpressVisible: boolean;
};

const ExpressCheckoutFrame = ({
  children,
  expressRef,
  isExpressCheckoutBlocked,
}: {
  children: ReactNode;
  expressRef: RefObject<HTMLDivElement>;
  isExpressCheckoutBlocked: boolean;
}) => (
  <div
    ref={expressRef}
    aria-disabled={isExpressCheckoutBlocked}
    data-testid='ShoppingBagExpressCheckout'
    style={isExpressCheckoutBlocked ? { pointerEvents: 'none' } : undefined}
  >
    {children}
  </div>
);

const AtomicExpressCheckout = ({
  areBraceletSizeErrorsSet,
  displayApplePayExpressButton,
  displayPaypalExpressButton,
  errorHandler,
  expressRef,
  isExpressCheckoutBlocked,
  isExpressVisible,
  mountExpressButtons,
  setApplePayStatus,
  setPaypalStatus,
  showLoadingPlaceholder,
  walletStatuses,
}: ExpressCheckoutBranchProps & {
  setApplePayStatus: (status: ExpressWalletStatus) => void;
  setPaypalStatus: (status: ExpressWalletStatus) => void;
  walletStatuses: WalletStatuses;
}) => {
  const presentation = getAtomicExpressPresentation({
    expectedStatuses: getExpectedWalletStatuses({
      displayApplePay: displayApplePayExpressButton,
      displayPaypal: displayPaypalExpressButton,
      statuses: walletStatuses,
    }),
    mountExpressButtons,
    showLoadingPlaceholder,
  });

  if (presentation === 'hidden') return null;

  const walletButtons =
    mountExpressButtons && isExpressVisible ? (
      <ExpressWalletButtons
        areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
        displayApplePay={displayApplePayExpressButton}
        displayPaypal={displayPaypalExpressButton}
        errorHandler={errorHandler}
        isExpressCheckoutBlocked={isExpressCheckoutBlocked}
        onApplePayStatusChange={setApplePayStatus}
        onPaypalStatusChange={setPaypalStatus}
      />
    ) : null;

  return (
    <ExpressCheckoutFrame
      expressRef={expressRef}
      isExpressCheckoutBlocked={isExpressCheckoutBlocked}
    >
      <ShoppingBag.ExpressSection
        loadingFallback={<Loader height={48} size='sm' />}
        presentation={presentation}
      >
        {walletButtons}
      </ShoppingBag.ExpressSection>
    </ExpressCheckoutFrame>
  );
};

const StandardExpressCheckout = ({
  areBraceletSizeErrorsSet,
  displayApplePayExpressButton,
  displayPaypalExpressButton,
  errorHandler,
  expressRef,
  isExpressCheckoutBlocked,
  isExpressVisible,
  mountExpressButtons,
  showLoadingPlaceholder,
}: ExpressCheckoutBranchProps) => {
  if (!mountExpressButtons) {
    if (!showLoadingPlaceholder) return null;

    return (
      <div
        aria-disabled='true'
        data-testid='ShoppingBagExpressCheckout'
        style={{ pointerEvents: 'none' }}
      >
        <ShoppingBag.ExpressSection>
          {displayPaypalExpressButton && <Loader height={48} size='sm' />}
          {displayApplePayExpressButton && <Loader height={48} size='sm' />}
        </ShoppingBag.ExpressSection>
      </div>
    );
  }

  return (
    <ExpressCheckoutFrame
      expressRef={expressRef}
      isExpressCheckoutBlocked={isExpressCheckoutBlocked}
    >
      <ShoppingBag.ExpressSection>
        {isExpressVisible ? (
          <ExpressWalletButtons
            areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
            displayApplePay={displayApplePayExpressButton}
            displayPaypal={displayPaypalExpressButton}
            errorHandler={errorHandler}
            isExpressCheckoutBlocked={isExpressCheckoutBlocked}
          />
        ) : (
          <Loader height={48} size='sm' />
        )}
      </ShoppingBag.ExpressSection>
    </ExpressCheckoutFrame>
  );
};

const ExpressCheckoutSection = ({
  areBraceletSizeErrorsSet,
  atomicExpressReveal,
  displayApplePayExpressButton,
  displayPaypalExpressButton,
  errorHandler,
  expressLazyMount,
  isExpressCheckoutBlocked,
  mountExpressButtons,
  showLoadingPlaceholder,
}: ExpressCheckoutSectionProps) => {
  const expressRef = useRef<HTMLDivElement>(null);
  const {
    setApplePayStatus,
    setPaypalStatus,
    statuses: walletStatuses,
  } = useExpressWalletStatuses({
    atomicExpressReveal,
    displayApplePay: displayApplePayExpressButton,
    displayPaypal: displayPaypalExpressButton,
    mountExpressButtons,
  });
  const expressEntry = useIntersectionObserver(
    expressLazyMount ? expressRef : null,
    {
      freezeOnceVisible: true,
      rootMargin: EXPRESS_SECTION_PRELOAD_MARGIN,
    },
  );
  const isExpressVisible =
    !expressLazyMount || (expressEntry?.isIntersecting ?? false);
  const branchProps = {
    areBraceletSizeErrorsSet,
    displayApplePayExpressButton,
    displayPaypalExpressButton,
    errorHandler,
    expressRef,
    isExpressCheckoutBlocked,
    isExpressVisible,
    mountExpressButtons,
    showLoadingPlaceholder,
  };

  if (!displayPaypalExpressButton && !displayApplePayExpressButton) {
    return null;
  }

  if (atomicExpressReveal) {
    return (
      <AtomicExpressCheckout
        {...branchProps}
        setApplePayStatus={setApplePayStatus}
        setPaypalStatus={setPaypalStatus}
        walletStatuses={walletStatuses}
      />
    );
  }

  return <StandardExpressCheckout {...branchProps} />;
};

const hasPartialPayment = (
  totalAuthorized?: { amount: number } | null,
  grossTotalPrice?: { amount: number } | null,
) => {
  const authorizedAmount = totalAuthorized?.amount;
  if (authorizedAmount === undefined || !(authorizedAmount > 0)) return false;

  const grossAmount = grossTotalPrice?.amount;
  if (grossAmount === undefined) return true;
  return grossAmount - authorizedAmount !== 0;
};

const getSubtotalBasis = <T,>(
  channel: string | undefined,
  subtotalGross: T,
  subtotal: T,
): T => {
  if (checkIfVatIncluded(channel)) return subtotalGross;
  return subtotal;
};

const getDisplayedTotal = (
  showTax: boolean,
  grossTotalPrice: MoneyFragment | undefined,
  netTotalPrice: MoneyFragment | undefined,
) => {
  if (showTax) return grossTotalPrice;
  return netTotalPrice;
};

const getToPayLater = (
  fragment: NonNullable<TransactionSummaryProps['fragment']>,
  preOrderData: TransactionSummaryProps['preOrderData'],
  showTax: boolean,
) => {
  if (isOrder(fragment)) {
    return getOrderToPayLater(fragment, preOrderData, showTax);
  }
  return getCheckoutToPayLater(fragment, preOrderData, showTax);
};

const isAmountWithinExpressLimit = (
  amount: number,
  minimum: number | null | undefined,
  maximum: number | null | undefined,
) => {
  const withinMaximum = maximum === null || amount <= (maximum ?? 0);
  const withinMinimum = minimum === null || amount >= (minimum ?? 0);
  return withinMaximum && withinMinimum;
};

const canShowPaypalExpressButton = ({
  countrySlug,
  localeSlug,
  maximum,
  minimum,
  netTotalPrice,
  showExpressButton,
}: {
  countrySlug: string;
  localeSlug: string;
  maximum: number | null | undefined;
  minimum: number | null | undefined;
  netTotalPrice?: { amount: number } | null;
  showExpressButton: boolean;
}) => {
  const isGlobalEMarket = GLOBAL_E_MARKET_AVAILABLE.includes(
    `${countrySlug}-${localeSlug}`,
  );
  if (isGlobalEMarket || !showExpressButton || !netTotalPrice) return false;
  return isAmountWithinExpressLimit(netTotalPrice.amount, minimum, maximum);
};

const canShowApplePayExpressButton = ({
  maximum,
  minimum,
  netTotalPrice,
  showButton,
  showExpressButton,
}: {
  maximum: number | null | undefined;
  minimum: number | null | undefined;
  netTotalPrice?: { amount: number } | null;
  showButton: boolean;
  showExpressButton: boolean;
}) => {
  if (!showButton || !showExpressButton || !netTotalPrice) return false;
  return isAmountWithinExpressLimit(netTotalPrice.amount, minimum, maximum);
};

const getPaymentMethodTotal = (
  preOrderData: TransactionSummaryProps['preOrderData'],
  depositAmount: number | undefined,
  grossAmount: number | undefined,
) => {
  if (preOrderData) return depositAmount;
  return grossAmount;
};

const PreOrderSummaryRows = ({
  depositPrice,
  fragment,
  isDeposit,
  preOrderData,
  toPayLater,
}: {
  depositPrice: ReturnType<typeof getDepositPrice>;
  fragment: NonNullable<TransactionSummaryProps['fragment']>;
  isDeposit: boolean;
  preOrderData: NonNullable<TransactionSummaryProps['preOrderData']>;
  toPayLater: ReturnType<typeof getToPayLater>;
}) => {
  if (isDeposit) {
    return (
      <>
        <ShoppingBag.ToPayLater
          data-testid='ShoppingBagToPayLater'
          price={toPayLater}
        />
        <ShoppingBag.Deposit
          data-testid='ShoppingBagDeposit'
          depositPercentage={preOrderData.deposit_percentage}
          price={depositPrice}
          type='already paid'
        />
        <ShoppingBag.PreOrderNote />
      </>
    );
  }

  if (!isOrder(fragment) || !toPayLater) return null;

  return (
    <>
      <ShoppingBag.Deposit
        data-testid='ShoppingBagOrderSummary-DepositAlreadyPaid'
        depositPercentage={preOrderData.deposit_percentage}
        price={depositPrice}
        type='already paid'
      />
      <ShoppingBag.ToPayNow
        data-testid='ShoppingBagOrderSummary-FullPaidMoney'
        price={toPayLater}
      />
    </>
  );
};

export const TransactionSummary: FC<TransactionSummaryProps> = ({
  areBraceletSizeErrorsSet,
  atomicExpressReveal = false,
  channel,
  coSalesTax,
  code,
  errorHandler,
  expressLazyMount = true,
  fragment,
  isCheckoutSelectedAddress = true,
  isColoradoBilling = false,
  isDeposit = true,
  isExpressCheckoutBlocked = false,
  isPricePending = false,
  isProcessing = false,
  mountExpressButtons = true,
  preOrderData,
  salesTax,
  setCheckoutValidationErrors,
  showButton = false,
  showCompactSummary = false,
  showExpressButton = false,
  showExpressLoadingPlaceholder = false,
  showGiftBox = false,
  showPadding = true,
  showPaymentLogos = true,
  showPaymentMethodsSection = true,
  showShippingAndTaxNote = true,
  showSubtotalAndTaxes = true,
  showTax = true,
  showTaxCalculationInfo,
  voucherType,
}) => {
  const formCtx = useForm();
  const { country, locale } = useRegionProvider();
  const {
    applepayExpressPaymentMax,
    applepayExpressPaymentMin,
    paypalExpressPaymentMax,
    paypalExpressPaymentMin,
  } = useConfigProvider();

  if (!fragment) return null;

  const checkBraceletSizeErrorsSet = areBraceletSizeErrorsSet ?? (() => true);

  const {
    discount,
    grossTotalPrice,
    netTotalPrice,
    shippingPrice,
    subtotal,
    subtotalGross,
    totalAuthorized,
  } = formatCheckoutOrder(fragment);
  const isPartiallyPaid = hasPartialPayment(totalAuthorized, grossTotalPrice);
  const depositPrice = getDepositPrice(fragment, preOrderData, showTax);
  const toPayLater = getToPayLater(fragment, preOrderData, showTax);
  const subtotalWithDiscount = calculateSubtotal(
    getSubtotalBasis(channel, subtotalGross, subtotal),
    discount,
    voucherType,
  );
  const hasWatch = (fragment.lines ?? [])
    .map((line) => line.variant?.product.productType.name?.toLowerCase())
    .includes(ProductTypeSlug.Watch);
  const totalPrice = getDisplayedTotal(showTax, grossTotalPrice, netTotalPrice);
  const displayPaypalExpressButton = canShowPaypalExpressButton({
    countrySlug: country.slug,
    localeSlug: locale.slug,
    maximum: paypalExpressPaymentMax,
    minimum: paypalExpressPaymentMin,
    netTotalPrice,
    showExpressButton,
  });
  const displayApplePayExpressButton = canShowApplePayExpressButton({
    maximum: applepayExpressPaymentMax,
    minimum: applepayExpressPaymentMin,
    netTotalPrice,
    showButton,
    showExpressButton,
  });

  return (
    <ShoppingBag.SummaryWrapper
      data-testid='ShoppingBagSummaryWrapper'
      showCompactSummary={showCompactSummary}
      showPadding={showPadding}
    >
      <FormProvider {...formCtx} data-test-id='ShoppingBagFormProvider'>
        {showSubtotalAndTaxes && (
          <>
            <ShoppingBag.SubtotalPrice
              data-testid='ShoppingBagSubtotalPrice'
              isLoading={isPricePending}
              price={subtotalWithDiscount}
            />

            {voucherType === VOUCHER_TYPE.FIXED &&
              discount &&
              discount.amount > 0 && (
                <ShoppingBag.Discount
                  data-testid='ShoppingBagDiscount'
                  price={getReverseDiscount(discount)}
                />
              )}

            <ShoppingBag.ShippingPrice
              data-testid='ShoppingBagShippingPrice'
              isLoading={isPricePending}
              price={shippingPrice}
            />

            {showTax && (
              <ShoppingBag.SalesTax
                channel={channel}
                data-testid='ShoppingBagSalesTax'
                displayZero={true}
                isLoading={isPricePending}
                price={salesTax}
              />
            )}
            {isColoradoBilling && coSalesTax?.amount !== 0 && (
              <ShoppingBag.RetailDeliveryFeesTax
                data-testid='ShoppingBagRetailDeliveryFeesTax'
                price={coSalesTax}
              />
            )}
          </>
        )}
        <ShoppingBag.TotalPrice
          channel={channel}
          data-testid='ShoppingBagTotalPrice'
          isCheckoutSelectedAddress={isCheckoutSelectedAddress}
          isLoading={isPricePending}
          netPrice={netTotalPrice}
          price={totalPrice}
          withBottomBorder={!!preOrderData}
          withTaxCalculationInfo={showTaxCalculationInfo}
        />
        {isPartiallyPaid && (
          <ShoppingBag.AlreadyPaidAmount
            data-testid='ShoppingBagDeposit'
            price={totalAuthorized}
            type='already paid'
          />
        )}
        {preOrderData && (
          <PreOrderSummaryRows
            depositPrice={depositPrice}
            fragment={fragment}
            isDeposit={isDeposit}
            preOrderData={preOrderData}
            toPayLater={toPayLater}
          />
        )}
        {showShippingAndTaxNote &&
          code ===
            COUNTRYCODE_TO_SHOW_SHIPPING_AND_TAXNOTE_MESSAGE.CountryCode && (
            <ShoppingBag.ShippingAndTaxNote data-testid='ShoppingBagShippingAndTaxNote' />
          )}
        {showGiftBox && <ShoppingBag.Gift />}
        {showButton && !isOrder(fragment) && (
          <ShoppingBag.Buttons
            areBraceletSizeErrorsSet={checkBraceletSizeErrorsSet}
            checkoutId={fragment.id}
            coupon={fragment.voucherCode ?? undefined}
            data-testid='ShoppingBagViewButtons'
            isDisabled={isProcessing}
            lines={fragment.lines}
            setCheckoutValidationErrors={setCheckoutValidationErrors}
            totalBalance={Math.abs(fragment.totalBalance?.amount as number)}
          />
        )}
        <ExpressCheckoutSection
          areBraceletSizeErrorsSet={checkBraceletSizeErrorsSet}
          atomicExpressReveal={atomicExpressReveal}
          displayApplePayExpressButton={!!displayApplePayExpressButton}
          displayPaypalExpressButton={!!displayPaypalExpressButton}
          errorHandler={errorHandler}
          expressLazyMount={expressLazyMount}
          isExpressCheckoutBlocked={isExpressCheckoutBlocked}
          mountExpressButtons={mountExpressButtons}
          showLoadingPlaceholder={showExpressLoadingPlaceholder}
        />
        {showPaymentMethodsSection && (
          <ShoppingBag.PaymentMethodsSection
            billingAddressCountryCode={fragment.billingAddress?.country.code}
            data-testid='ShoppingBagPaymentMethodsSection'
            hasWatch={hasWatch}
            id={fragment?.id}
            isDepositPaid={!isDeposit}
            isPreOrder={!!preOrderData}
            showCompactSummary={showCompactSummary}
            showLogos={showPaymentLogos}
            totalPrice={getPaymentMethodTotal(
              preOrderData,
              depositPrice?.amount,
              grossTotalPrice?.amount,
            )}
          />
        )}
      </FormProvider>
    </ShoppingBag.SummaryWrapper>
  );
};
